import axios from "axios"
import type { NavigateFunction } from "react-router-dom"
import apiClient, { API_BASE } from "./apiClient"

/** Returns full URL for profile image. External URLs (http/https) returned as-is. Relative paths (/uploads/...) are always prefixed with API_BASE so images load from backend (e.g. localhost:8001). */
export const getProfileImageUrl = (url?: string | null): string | null => {
  if (!url || typeof url !== "string") return null
  const u = String(url).trim()
  if (!u) return null
  if (u.startsWith("http://") || u.startsWith("https://")) return u
  let path = u.startsWith("/") ? u : "/" + u
  if (path.startsWith("/uploads/") && !path.includes("/user/")) {
    path = "/uploads/user" + path.slice("/uploads".length)
  }
  return API_BASE.replace(/\/$/, "") + path
}

/** Returns full URL for KYC assets (selfie, signature, video). Path is e.g. /uploads/kyc/filename. Always uses API_BASE so assets load from backend. */
export const getKycAssetUrl = (path?: string | null): string | null => {
  if (!path || typeof path !== "string") return null
  const p = String(path).trim()
  if (!p) return null
  if (p.startsWith("http://") || p.startsWith("https://")) return p
  const normalized = p.startsWith("/") ? p : "/" + p
  return API_BASE.replace(/\/$/, "") + normalized
}

/** Known field names that may contain profile image path/URL */
const PROFILE_IMAGE_KEYS = [
  "profileImageUrl",
  "profile_image_url",
  "profileImage",
  "profile_image",
  "avatar",
  "avatarUrl",
  "avatar_url",
  "image",
  "imageUrl",
  "image_url",
  "photo",
  "photoUrl",
  "photo_url",
  "picture",
  "pictureUrl",
]

/** Get profile image URL from user object - checks multiple possible field names */
export const getUserProfileImageUrl = (user?: Record<string, any> | null): string | null => {
  if (!user) return null
  // Check top-level keys
  for (const key of PROFILE_IMAGE_KEYS) {
    const val = user[key]
    if (typeof val === "string" && val.trim()) {
      const url = getProfileImageUrl(val)
      if (url) return url
    }
  }
  // Check nested user.profile
  const profile = user.profile
  if (profile && typeof profile === "object") {
    for (const key of PROFILE_IMAGE_KEYS) {
      const val = profile[key]
      if (typeof val === "string" && val.trim()) {
        const url = getProfileImageUrl(val)
        if (url) return url
      }
    }
  }
  return null
}

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponseUser {
  _id: string
  fullName: string
  email: string
  role: string
  mobileNumber?: string
  state?: string
  city?: string
  address?: string
  aadharNumber?: string
  documents?: any[]
  createdAt?: string
  updatedAt?: string
  tokenVersion?: number
  kycSelfieDone?: boolean
  kycSignatureDone?: boolean
  /** Documents uploaded (selfie + signature); still needs admin approval for platform access */
  kycSubmitted?: boolean
  kycApprovedByAdmin?: boolean
  kycComplete?: boolean
  profileComplete?: boolean
  [key: string]: any
}

/** When requiresOtp is true, OTP is sent to email; use verifyLoginOtpApi to complete login. */
export interface LoginResponse {
  success: boolean
  requiresOtp?: boolean
  message?: string
  data?: LoginResponseUser
  token?: string
}

export interface VerifyLoginOtpRequest {
  email: string
  otp: string
}

export const verifyLoginOtpApi = async (
  body: VerifyLoginOtpRequest
): Promise<{ success: boolean; data: LoginResponseUser; token: string; message?: string }> => {
  const { data } = await axios.post<{ success: boolean; data: LoginResponseUser; token: string; message?: string }>(
    `${API_BASE}/api/auth/verify-login-otp`,
    body,
    { headers: { "Content-Type": "application/json" } }
  )
  return data
}

export interface GetUserResponse {
  success: boolean
  data: Record<string, any>
  message?: string
}

export const getUserApi = async (userId: string): Promise<GetUserResponse> => {
  const { data } = await apiClient.get<GetUserResponse>(`/api/users/getUserById/${userId}`)
  return data
}

export interface GetUsersResponse {
  success: boolean
  data: Array<Record<string, any>>
  message?: string
}

/** Get all users (admin) – includes KYC flags: kycSelfieDone, kycSignatureDone, kycComplete, kycSelfieUrl, kycSignatureUrl, kycVideoUrl, isBlocked */
export const getUsersApi = async (): Promise<GetUsersResponse> => {
  const { data } = await apiClient.get<GetUsersResponse>("/api/users/getUsers")
  return data
}

/** Delete user (admin). Requires JWT. */
export const deleteUserApi = async (userId: string): Promise<{ success: boolean; message?: string }> => {
  const { data } = await apiClient.delete<{ success: boolean; message?: string }>(`/api/users/deleteUser/${userId}`)
  return data
}

/** Block or unblock user (admin). Blocked users cannot login. */
export const blockUserApi = async (userId: string, blocked: boolean): Promise<{ success: boolean; data?: any; message?: string }> => {
  const { data } = await apiClient.patch<{ success: boolean; data?: any; message?: string }>(`/api/users/blockUser/${userId}`, { blocked })
  return data
}

/** Admin: approve or revoke user KYC (platform access). Omit approved in body to approve. */
export const approveUserKycApi = async (
  userId: string,
  approved: boolean
): Promise<{ success: boolean; data?: any; message?: string }> => {
  const { data } = await apiClient.patch<{ success: boolean; data?: any; message?: string }>(
    `/api/users/approveKyc/${userId}`,
    { approved }
  )
  return data
}

export const loginApi = async (
  body: LoginRequest
): Promise<LoginResponse> => {
  const { data } = await axios.post<LoginResponse>(
    `${API_BASE}/api/auth/login`,
    body,
    { headers: { "Content-Type": "application/json" } }
  )
  return data
}

export type SocialLoginType = "Buyer" | "Seller" | "Broker"

export interface PostSocialLoginData {
  credential: string
  type?: SocialLoginType
}

/** Thrown when backend returns requiresRoleSelection (new user must choose Buyer/Seller/Broker). */
export class RequiresRoleSelectionError extends Error {
  requiresRoleSelection = true
  constructor(message: string) {
    super(message)
    this.name = "RequiresRoleSelectionError"
  }
}

export const postSocialLogin = async (data: PostSocialLoginData) => {
  const body: { credential: string; type?: SocialLoginType } = { credential: data.credential }
  if (data.type) body.type = data.type

  const res = await fetch(`${API_BASE}/api/auth/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  })
  const json = await res.json()
  if (!json.success) {
    if (json.requiresRoleSelection) {
      throw new RequiresRoleSelectionError(json.message || "Select your role to continue.")
    }
    throw new Error(json.message || "Google login failed")
  }
  const d = json.data
  return {
    ...d,
    uid: d._id,
    username: d.fullName,
    accessToken: json.token,
    token: json.token,
    profileImageUrl:
      d.profileImageUrl ?? d.profile_image_url ?? d.avatar ?? d.profileImage ??
      (d.profile?.profileImageUrl ?? d.profile?.avatar ?? d.profile?.imageUrl),
  }
}

export interface ProfileUpdateRequest {
  fullName?: string
  email?: string
  mobileNumber?: string
  state?: string
  city?: string
  address?: string
}

export interface ProfileUpdateResponse {
  success: boolean
  data?: Record<string, any>
  message?: string
}

export const updateProfileApi = async (body: ProfileUpdateRequest): Promise<ProfileUpdateResponse> => {
  const { data } = await apiClient.post<ProfileUpdateResponse>("/api/auth/profile", body, {
    headers: { "Content-Type": "application/json" },
  })
  return data
}

export interface UpdateUserFormData {
  id: string
  fullName?: string
  email?: string
  role?: string
  mobileNumber?: string
  state?: string
  city?: string
  address?: string
  pinCode?: string
  aadharNumber?: string
  password?: string
  profileImage?: File
  documents?: File[]
}

export const updateUserApi = async (formData: UpdateUserFormData): Promise<ProfileUpdateResponse> => {
  const fd = new FormData()
  fd.append("id", formData.id)
  if (formData.fullName) fd.append("fullName", formData.fullName)
  if (formData.email) fd.append("email", formData.email)
  if (formData.role) fd.append("role", formData.role)
  if (formData.mobileNumber) fd.append("mobileNumber", formData.mobileNumber)
  if (formData.state) fd.append("state", formData.state)
  if (formData.city) fd.append("city", formData.city)
  if (formData.address) fd.append("address", formData.address)
  if (formData.pinCode) fd.append("pinCode", formData.pinCode)
  if (formData.aadharNumber) fd.append("aadharNumber", formData.aadharNumber)
  if (formData.password) fd.append("password", formData.password)
  if (formData.profileImage) fd.append("profileImage", formData.profileImage)
  if (formData.documents?.length) {
    formData.documents.forEach((f) => fd.append("documents", f))
  }
  const { data } = await apiClient.post<ProfileUpdateResponse>("/api/users/createUser", fd)
  return data
}

/** Create new user (admin only). Role is fixed to Admin. */
export interface CreateUserFormData {
  fullName: string
  email: string
  password: string
  role: string
  mobileNumber?: string
}

export const createUserApi = async (formData: CreateUserFormData): Promise<ProfileUpdateResponse> => {
  const fd = new FormData()
  fd.append("fullName", formData.fullName)
  fd.append("email", formData.email)
  fd.append("password", formData.password)
  fd.append("role", formData.role)
  if (formData.mobileNumber) fd.append("mobileNumber", formData.mobileNumber)
  const { data } = await apiClient.post<ProfileUpdateResponse>("/api/users/createUser", fd)
  return data
}

export interface KycFormData {
  kycSelfie: File
  kycSignature?: File
  kycVideo?: File
}

export const submitKycApi = async (userId: string, files: KycFormData): Promise<ProfileUpdateResponse> => {
  const fd = new FormData()
  fd.append("kycSelfie", files.kycSelfie)
  if (files.kycSignature) fd.append("kycSignature", files.kycSignature)
  if (files.kycVideo) fd.append("kycVideo", files.kycVideo)
  const { data } = await apiClient.post<ProfileUpdateResponse>(`/api/users/uploadKyc/${userId}`, fd)
  return data
}

// Forgot password – request reset link
export interface ForgotPasswordRequest {
  email: string
}

export interface ForgotPasswordResponse {
  success: boolean
  message: string
}

export const forgotPasswordApi = async (body: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
  const { data } = await apiClient.post<ForgotPasswordResponse>("/api/auth/forgot-password", body, {
    headers: { "Content-Type": "application/json" },
  })
  return data
}

// Reset password – with token from email link
export interface ResetPasswordRequest {
  token: string
  newPassword: string
}

export interface ResetPasswordResponse {
  success: boolean
  message?: string
}

export const resetPasswordApi = async (body: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
  const { data } = await apiClient.post<ResetPasswordResponse>("/api/auth/reset-password", body, {
    headers: { "Content-Type": "application/json" },
  })
  return data
}

// Send OTP (e.g. for register)
export interface SendOtpRequest {
  email: string
  purpose: "register"
}

export interface SendOtpResponse {
  success: boolean
  message?: string
}

export const sendOtpApi = async (body: SendOtpRequest): Promise<SendOtpResponse> => {
  const { data } = await axios.post<SendOtpResponse>(`${API_BASE}/api/auth/send-otp`, body, {
    headers: { "Content-Type": "application/json" },
  })
  return data
}

// Register (after OTP sent)
export type RegisterType = "Buyer" | "Seller" | "Broker"

export interface RegisterRequest {
  fullName: string
  email: string
  type: RegisterType
  password: string
  otp: string
}

export interface RegisterResponse {
  success: boolean
  data: LoginResponseUser
  token: string
  message?: string
}

export const registerApi = async (body: RegisterRequest): Promise<RegisterResponse> => {
  const { data } = await axios.post<RegisterResponse>(`${API_BASE}/api/auth/register`, body, {
    headers: { "Content-Type": "application/json" },
  })
  return data
}

export function getStoredAuthUser(): LoginResponseUser | null {
  try {
    const raw = localStorage.getItem("authUser")
    if (!raw) return null
    return JSON.parse(raw) as LoginResponseUser
  } catch {
    return null
  }
}

/** Fired after authUser is refreshed in localStorage (profile save, layout sync, etc.). */
export const AUTH_USER_UPDATED_EVENT = "profileUpdated"

/** Refresh authUser from API so role/KYC changes reflect without visiting profile first. */
export async function syncAuthUserFromApi(): Promise<LoginResponseUser | null> {
  const stored = getStoredAuthUser()
  if (!stored) return null

  const uid = String(stored.uid ?? stored._id ?? "").trim()
  const token = stored.accessToken || stored.token
  if (!uid || !token) return stored

  try {
    const res = await getUserApi(uid)
    if (!res?.success || !res?.data) return stored

    const d = res.data
    const updated: LoginResponseUser = {
      ...stored,
      ...d,
      fullName: String(d.fullName ?? d.username ?? stored.fullName ?? ""),
      username: String(d.fullName ?? d.username ?? stored.fullName ?? ""),
      uid: (d._id as string) ?? uid,
      _id: (d._id as string) ?? uid,
      role: String(d.role ?? stored.role ?? ""),
      accessToken: token,
      token,
    }
    localStorage.setItem("authUser", JSON.stringify(updated))
    window.dispatchEvent(new CustomEvent(AUTH_USER_UPDATED_EVENT))
    return updated
  } catch {
    return stored
  }
}

function isAdminUserRole(role?: string): boolean {
  const r = normalizeUserRole(role)
  return r === "admin" || r === "super-admin" || r === "superadmin"
}

export function normalizeUserRole(role?: string): string {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/_/g, "-")
}

/** After login/register: buyers → home, broker/seller/admin → dashboard. */
export function getPostLoginRedirectPath(role?: string): string {
  const r = normalizeUserRole(role)
  if (r === "buyer") return "/"
  if (r === "broker" || r === "seller" || isAdminUserRole(r)) return "/dashboard"
  return "/"
}

export type PropertyKycBlockReason = "upload" | "pending"

export type PropertyDetailGateReason = "login" | PropertyKycBlockReason | null

/** Full platform access: admin bypass, or kycComplete (submitted + admin approved). */
export function getPropertyDetailGateReason(): PropertyDetailGateReason {
  const u = getStoredAuthUser()
  if (!u) return "login"
  if (isAdminUserRole(u.role)) return null
  if (u.kycComplete === true) return null
  const submitted =
    u.kycSubmitted === true ||
    !!(u.profileComplete && u.kycSelfieDone && u.kycSignatureDone)
  if (submitted && u.kycApprovedByAdmin !== true) return "pending"
  return "upload"
}

export type NavigateToPropertyDetailOptions = {
  /** Logged in but not approved for property access — open modal with the right copy. */
  onKycRequired?: (reason: PropertyKycBlockReason) => void
}

/** Open listing detail only if allowed; otherwise login, or invoke onKycRequired and stay here. */
export function navigateToPropertyDetail(
  navigate: NavigateFunction,
  listingId: string,
  options?: NavigateToPropertyDetailOptions
): void {
  const gate = getPropertyDetailGateReason()
  if (gate === "login") {
    navigate("/login", { state: { from: { pathname: `/property/${listingId}` } } })
    return
  }
  if (gate === "upload" || gate === "pending") {
    options?.onKycRequired?.(gate)
    return
  }
  navigate(`/property/${listingId}`)
}
